import React, { useState, useMemo, ReactNode } from 'react';
import styled from 'astroturf/react';

import { Body } from 'js/design_system/Body';
import {
  BundleWork,
  CATEGORY_BUCKETS,
} from 'js/bulk_download/work_cards_shared/types';
import { WorkCard } from 'js/bulk_download/work_cards_shared/WorkCard';
import { FadeFooter } from 'js/bulk_download/work_cards_shared/PaperPreviewList';

interface Props {
  relatedWorks: BundleWork[];
  sourceWorkPreview: BundleWork | undefined;
  includedWorkSlot: ReactNode;
  showAnnotations: boolean;
  badgePlacement: 'side' | 'below' | 'above';
  emptyState: ReactNode;
  paperLimit: number;
  totalRelatedCount: number;
}

const BUCKET_TO_TAB: Record<string, string> = {
  citingThis: 'Citing',
  citedByThis: 'Cited by',
  topRelated: 'Similar',
  sameAuthor: 'By author',
};

export function TabbedPreviewList({
  relatedWorks,
  includedWorkSlot,
  showAnnotations,
  badgePlacement,
  emptyState,
  paperLimit,
}: Props) {
  const bucketCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const work of relatedWorks) {
      if (work.bucket && work.bucket !== 'thePaperItself') {
        counts[work.bucket] = (counts[work.bucket] ?? 0) + 1;
      }
    }
    return counts;
  }, [relatedWorks]);

  const availableTabs = useMemo(
    () =>
      CATEGORY_BUCKETS.filter(({ key }) => (bucketCounts[key] ?? 0) > 0).map(
        ({ key }) => key,
      ),
    [bucketCounts],
  );

  const [activeTab, setActiveTab] = useState<string | 'all'>('all');

  const filteredWorks = useMemo(() => {
    if (activeTab === 'all') return relatedWorks;
    return relatedWorks.filter((w) => w.bucket === activeTab);
  }, [relatedWorks, activeTab]);

  const visibleWorks = filteredWorks.slice(0, paperLimit);
  const hiddenCount = filteredWorks.length - visibleWorks.length;

  return (
    <Container>
      <TabBar>
        <Tab
          type="button"
          data-active={activeTab === 'all' || undefined}
          onClick={() => setActiveTab('all')}
        >
          <Body size="xs" bold={activeTab === 'all'} as="span">
            All ({relatedWorks.length})
          </Body>
        </Tab>
        {availableTabs.map((key) => (
          <Tab
            key={key}
            type="button"
            data-active={activeTab === key || undefined}
            onClick={() => setActiveTab(key)}
          >
            <Body size="xs" bold={activeTab === key} as="span">
              {BUCKET_TO_TAB[key] ?? key} ({bucketCounts[key]})
            </Body>
          </Tab>
        ))}
      </TabBar>
      <ScrollArea>
        {activeTab === 'all' && includedWorkSlot}
        {visibleWorks.length === 0 && emptyState ? (
          <>{emptyState}</>
        ) : (
          visibleWorks.map((work) => (
            <WorkCard
              key={work.id}
              work={work}
              showAnnotations={showAnnotations}
              badgePlacement={badgePlacement}
              showBucketBadge
            />
          ))
        )}
      </ScrollArea>
      {hiddenCount > 0 && <FadeFooter hiddenPapersCount={hiddenCount} />}
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  position: relative;
`;

const TabBar = styled.div`
  display: flex;
  gap: 0;
  border-bottom: 1px solid var(--brand-colors-neutral-400, #dddde2);
  overflow-x: auto;
  flex-shrink: 0;
`;

const Tab = styled.button`
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  padding: var(--spacing-xs-8, 8px) var(--spacing-xs-12, 12px);
  cursor: pointer;
  white-space: nowrap;
  color: var(--text-secondary, #535366);
  transition:
    border-color 0.15s,
    color 0.15s;

  &[data-active] {
    border-bottom-color: var(--primary-brand, #0645b1);
    color: var(--text-primary, #141413);
  }

  &:hover:not([data-active]) {
    border-bottom-color: var(--brand-colors-neutral-500, #b3b3be);
  }
`;

const ScrollArea = styled.div`
  flex: 1;
  overflow-y: auto;
  min-height: 0;
`;
